'use client';

import Link from 'next/link';

import { ArrowUpRight } from 'lucide-react';

import { buildBazaLekowUrl } from '@kit/drug-interactions/lib/slug';
import type { Json } from '@kit/supabase/database';

export interface DrugLookup {
  [drugId: string]: {
    name: string;
    commonName: string | null;
    strength: string | null;
    form: string | null;
  };
}

interface SyncRunDrugDetail {
  id: string;
  name: string;
  commonName: string | null;
  strength: string | null;
  form: string | null;
}

interface DrugChangeEntry {
  id: string;
  name: string;
  commonName?: string;
  changes: {
    field: string;
    oldValue: string | null;
    newValue: string | null;
  }[];
  // Tagged by orchestrator after Step 3b. Missing on legacy rows — render those as before.
  // Legacy values (`layer1_fork_copy`, `layer2_short_circuit`) coexist with the new vocabulary
  // for rows written before the dedup-v2 rename.
  extractionOutcome?:
    | 'llm'
    | 'cross_section_dedup'
    | 'chapter_45_verified'
    | 'failed'
    | 'not_extracted'
    | 'layer1_fork_copy'
    | 'layer2_short_circuit';
}

interface SyncRun {
  id: string;
  started_at: string;
  completed_at: string | null;
  new_drugs: Json;
  updated_drugs: Json;
  updated_drugs_metadata: Json;
  updated_drugs_chpl: Json;
  deactivated_drugs: Json;
  step_stats: Json;
}

interface DrugWithReason extends SyncRunDrugDetail {
  reason: string;
}

/** Resolve drug details from lookup, falling back to stored snapshot */
function resolveDrug(
  stored: {
    id: string;
    name: string;
    commonName?: string | null;
    strength?: string | null;
    form?: string | null;
  },
  lookup: DrugLookup,
): SyncRunDrugDetail {
  const live = lookup[stored.id];
  return {
    id: stored.id,
    name: live?.name ?? stored.name,
    commonName: live?.commonName ?? stored.commonName ?? null,
    strength: live?.strength ?? stored.strength ?? null,
    form: live?.form ?? stored.form ?? null,
  };
}

function formatDate(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString('pl-PL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatTime(isoDate: string): string {
  return new Date(isoDate).toLocaleTimeString('pl-PL', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function truncate(val: string, max: number): string {
  return val.length > max ? val.slice(0, max) + '…' : val;
}

function pluralPl(n: number, one: string, few: string, many: string): string {
  if (n === 1) return one;
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

function DrugLinkButton({ href }: { href: string }) {
  return (
    <Link
      href={href}
      title="Zobacz lek"
      className="border-muted-foreground/40 shadow-muted-foreground/25 text-muted-foreground/60 hover:bg-foreground hover:text-background hover:border-foreground hover:shadow-foreground/25 inline-flex items-center rounded border px-1 py-0.5 text-[10px] leading-none font-bold tracking-wide uppercase shadow-[1.5px_1.5px_0_0] transition-all hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[0.5px_0.5px_0_0]"
    >
      <ArrowUpRight className="h-2.5 w-2.5" />
    </Link>
  );
}

function ReasonBadges({ drug }: { drug: DrugWithReason }) {
  const reasons = drug.reason.split(', ');
  const hasChpl = reasons.includes('zmiana ChPL');
  const hasMeta = reasons.includes('zmiana metadanych');
  return (
    <div className="flex flex-wrap gap-1">
      {hasChpl && (
        <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] leading-none font-medium text-blue-700 dark:bg-blue-900/40 dark:text-blue-400">
          ChPL
        </span>
      )}
      {hasMeta && (
        <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] leading-none font-medium text-amber-700 dark:bg-amber-900/40 dark:text-amber-400">
          metadane
        </span>
      )}
    </div>
  );
}

function DrugCard({
  drug,
  showReason,
  noLinks,
}: {
  drug: SyncRunDrugDetail | DrugWithReason;
  showReason?: boolean;
  noLinks?: boolean;
}) {
  const href = noLinks
    ? undefined
    : buildBazaLekowUrl([
        {
          id: drug.id,
          product_name: drug.name,
          common_name: drug.commonName,
          strength: drug.strength,
          form: drug.form,
        },
      ]);

  return (
    <div className="border-muted/40 flex items-start justify-between gap-2 border-b px-4 py-2.5 last:border-0">
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="text-foreground text-sm leading-snug font-medium">
          {drug.name}
        </div>
        <div className="text-muted-foreground text-xs">
          {drug.commonName ?? '—'}
        </div>
        <div className="text-muted-foreground/70 flex flex-wrap gap-x-3 text-xs">
          {drug.strength && <span>{drug.strength}</span>}
          {drug.form && <span>{truncate(drug.form, 25)}</span>}
        </div>
        {showReason && 'reason' in drug && (
          <div className="pt-0.5">
            <ReasonBadges drug={drug} />
          </div>
        )}
      </div>
      {href && (
        <div className="pt-1">
          <DrugLinkButton href={href} />
        </div>
      )}
    </div>
  );
}

function DrugTable({
  drugs,
  showReason,
  noLinks,
}: {
  drugs: (SyncRunDrugDetail | DrugWithReason)[];
  showReason?: boolean;
  noLinks?: boolean;
}) {
  return (
    <>
      {/* Mobile: card layout */}
      <div className="md:hidden">
        {drugs.map((drug) => (
          <DrugCard
            key={drug.id}
            drug={drug}
            showReason={showReason}
            noLinks={noLinks}
          />
        ))}
      </div>

      {/* Desktop: table layout */}
      <table className="hidden w-full text-sm md:table">
        <thead>
          <tr className="text-muted-foreground/40 border-b text-left text-[11px] tracking-wider uppercase">
            <th className="w-[28%] py-1 pl-5 font-medium">Lek</th>
            <th className="py-1 font-medium">Substancja</th>
            <th className="py-1 font-medium">Dawka</th>
            <th className="py-1 font-medium">Postać</th>
            {showReason && <th className="py-1 font-medium">Zmiana</th>}
            {!noLinks && <th className="w-8 py-1"></th>}
          </tr>
        </thead>
        <tbody>
          {drugs.map((drug) => {
            const href = noLinks
              ? undefined
              : buildBazaLekowUrl([
                  {
                    id: drug.id,
                    product_name: drug.name,
                    common_name: drug.commonName,
                    strength: drug.strength,
                    form: drug.form,
                  },
                ]);

            return (
              <tr
                key={drug.id}
                className="border-muted/40 border-b last:border-0"
              >
                <td className="py-1.5 pr-2 pl-5">
                  <span className="text-foreground font-medium">
                    {drug.name}
                  </span>
                </td>
                <td className="text-muted-foreground py-1.5 pr-2">
                  {drug.commonName ? truncate(drug.commonName, 35) : '—'}
                </td>
                <td className="text-muted-foreground/70 py-1.5 pr-2">
                  {drug.strength ? truncate(drug.strength, 25) : '—'}
                </td>
                <td className="text-muted-foreground/70 py-1.5 pr-2">
                  {drug.form ? truncate(drug.form, 20) : '—'}
                </td>
                {showReason && (
                  <td className="py-1.5 pr-2">
                    {'reason' in drug && <ReasonBadges drug={drug} />}
                  </td>
                )}
                {href && (
                  <td className="py-1.5">
                    <DrugLinkButton href={href} />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

interface DrugFieldChange {
  field: string;
  oldValue: string | null;
  newValue: string | null;
}

// Keep in sync with apps/web/scripts/send-sync-notification.ts
const CHPL_FIELDS = new Set([
  'chpl_url_pl',
  'doc_url_eu',
  'is_active_eu',
  'eu_status',
]);

function classifyFieldChanges(changes: DrugFieldChange[]): {
  hasChpl: boolean;
  hasMeta: boolean;
} {
  let hasChpl = false;
  let hasMeta = false;
  for (const c of changes) {
    if (CHPL_FIELDS.has(c.field)) {
      hasChpl = true;
    } else {
      hasMeta = true;
    }
  }
  return { hasChpl, hasMeta };
}

function buildUpdatedDrugsWithReasons(
  run: SyncRun,
  drugLookup: DrugLookup,
): DrugWithReason[] {
  const updatedDrugs = (run.updated_drugs as SyncRunDrugDetail[] | null) ?? [];
  const stats = (run.step_stats as Record<string, unknown> | null) ?? {};
  const step1 = stats.step1 as Record<string, unknown> | null;
  const step2 = stats.step2 as Record<string, unknown> | null;
  const fieldChanges =
    (step1?.updatedDrugFields as Record<string, DrugFieldChange[]> | null) ??
    null;
  const pdfDrugs = (step2?.changedDrugs as SyncRunDrugDetail[] | null) ?? [];

  const reasonMap = new Map<
    string,
    { drug: SyncRunDrugDetail; reasons: Set<string> }
  >();

  for (const drug of updatedDrugs) {
    const resolved = resolveDrug(drug, drugLookup);
    const entry = reasonMap.get(drug.id) ?? {
      drug: resolved,
      reasons: new Set<string>(),
    };
    const changes = fieldChanges?.[drug.id];
    if (changes && changes.length > 0) {
      const { hasChpl, hasMeta } = classifyFieldChanges(changes);
      if (hasChpl) entry.reasons.add('zmiana ChPL');
      if (hasMeta) entry.reasons.add('zmiana metadanych');
    } else {
      entry.reasons.add('zmiana metadanych');
    }
    reasonMap.set(drug.id, entry);
  }

  for (const drug of pdfDrugs) {
    const resolved = resolveDrug(drug, drugLookup);
    const entry = reasonMap.get(drug.id) ?? {
      drug: resolved,
      reasons: new Set<string>(),
    };
    entry.reasons.add('zmiana ChPL');
    reasonMap.set(drug.id, entry);
  }

  return Array.from(reasonMap.values()).map(({ drug, reasons }) => ({
    ...drug,
    reason: Array.from(reasons).join(', '),
  }));
}

function SectionHeader({
  symbol,
  count,
  label,
}: {
  symbol: string;
  count: number;
  label: string;
}) {
  return (
    <div className="mt-5">
      <div className="mb-0 flex">
        <div className="border-foreground bg-foreground text-background flex items-baseline gap-1.5 border-2 border-b-0 px-3 py-1.5 sm:gap-2 sm:px-4">
          <span className="text-base leading-none font-black sm:text-lg">
            {symbol}
            {count}
          </span>
          <span className="text-[10px] font-bold tracking-widest uppercase sm:text-xs">
            {label}
          </span>
        </div>
      </div>
      <div className="border-foreground border-t-2 pt-2" />
    </div>
  );
}

function SyncRunCard({
  run,
  drugLookup,
}: {
  run: SyncRun;
  drugLookup: DrugLookup;
}) {
  const rawNewDrugs = (run.new_drugs as SyncRunDrugDetail[] | null) ?? [];
  const rawDeactivatedDrugs =
    (run.deactivated_drugs as SyncRunDrugDetail[] | null) ?? [];

  const newDrugs = rawNewDrugs.map((d) => resolveDrug(d, drugLookup));
  const deactivatedDrugs = rawDeactivatedDrugs.map((d) =>
    resolveDrug(d, drugLookup),
  );

  const excludeFromUpdated = new Set([
    ...newDrugs.map((d) => d.id),
    ...deactivatedDrugs.map((d) => d.id),
  ]);

  // New categorized columns
  const updatedDrugsMetadata =
    (run.updated_drugs_metadata as DrugChangeEntry[] | null) ?? [];
  const rawUpdatedDrugsChpl =
    (run.updated_drugs_chpl as DrugChangeEntry[] | null) ?? [];
  // Hide entries that were merely re-verified by chapter-4.5 hash match (PDF
  // re-published but chapter 4.5 byte-identical). Those carry no actionable
  // change for users. The legacy `layer2_short_circuit` value is kept in the
  // filter set for backward-compat with rows written before the dedup-v2 rename.
  // `failed` is a defense-in-depth safety net — the sync orchestrator already
  // drops failed/sectionless drugs, but a failed resolution must never render
  // as a usable change. Legacy rows with no extractionOutcome render unchanged.
  const updatedDrugsChpl = rawUpdatedDrugsChpl.filter(
    (e) =>
      e.extractionOutcome !== 'chapter_45_verified' &&
      e.extractionOutcome !== 'layer2_short_circuit' &&
      e.extractionOutcome !== 'failed',
  );
  const hasNewFormat =
    updatedDrugsMetadata.length > 0 ||
    rawUpdatedDrugsChpl.length > 0 ||
    run.updated_drugs_metadata !== null;

  let allUpdatedDrugs: DrugWithReason[];
  if (hasNewFormat) {
    const chplIds = new Set(updatedDrugsChpl.map((d) => d.id));
    const metaIds = new Set(updatedDrugsMetadata.map((d) => d.id));
    const deduped = new Map<string, DrugWithReason>();

    for (const d of updatedDrugsMetadata) {
      const reasons = ['zmiana metadanych'];
      if (chplIds.has(d.id)) reasons.push('zmiana ChPL');
      deduped.set(d.id, {
        ...resolveDrug(d, drugLookup),
        reason: reasons.join(', '),
      });
    }
    for (const d of updatedDrugsChpl) {
      if (!metaIds.has(d.id)) {
        deduped.set(d.id, {
          ...resolveDrug(d, drugLookup),
          reason: 'zmiana ChPL',
        });
      }
    }
    allUpdatedDrugs = Array.from(deduped.values());
  } else {
    // Legacy format
    allUpdatedDrugs = buildUpdatedDrugsWithReasons(run, drugLookup);
  }
  allUpdatedDrugs = allUpdatedDrugs.filter(
    (d) => !excludeFromUpdated.has(d.id),
  );

  const hasChanges =
    newDrugs.length > 0 ||
    allUpdatedDrugs.length > 0 ||
    deactivatedDrugs.length > 0;

  return (
    <div className="relative space-y-1 rounded-lg border-2 border-stone-300 p-3 shadow-[4px_4px_0_0_#d6d3d1] sm:p-5 dark:border-stone-700 dark:shadow-[4px_4px_0_0_#44403c]">
      <h3 className="font-heading absolute top-3 right-3 text-base font-black tracking-tight sm:top-4 sm:right-5 sm:text-xl">
        {formatDate(run.completed_at ?? run.started_at)}
        {run.completed_at && (
          <span className="text-muted-foreground ml-1 text-xs font-normal sm:ml-1.5 sm:text-base">
            {formatTime(run.completed_at)}
          </span>
        )}
      </h3>

      {hasChanges ? (
        <div className="pt-7 sm:pt-5">
          {newDrugs.length > 0 && (
            <>
              <SectionHeader
                symbol="+"
                count={newDrugs.length}
                label={pluralPl(
                  newDrugs.length,
                  'nowy lek',
                  'nowe leki',
                  'nowych leków',
                )}
              />
              <DrugTable drugs={newDrugs} />
            </>
          )}
          {allUpdatedDrugs.length > 0 && (
            <>
              <SectionHeader
                symbol="↻"
                count={allUpdatedDrugs.length}
                label={pluralPl(
                  allUpdatedDrugs.length,
                  'zaktualizowany',
                  'zaktualizowane',
                  'zaktualizowanych',
                )}
              />
              <DrugTable drugs={allUpdatedDrugs} showReason />
            </>
          )}
          {deactivatedDrugs.length > 0 && (
            <>
              <SectionHeader
                symbol="−"
                count={deactivatedDrugs.length}
                label={pluralPl(
                  deactivatedDrugs.length,
                  'usunięty',
                  'usunięte',
                  'usuniętych',
                )}
              />
              <DrugTable drugs={deactivatedDrugs} noLinks />
            </>
          )}
        </div>
      ) : (
        <p className="text-muted-foreground text-sm">
          Brak zmian w tej synchronizacji.
        </p>
      )}
    </div>
  );
}

export function SyncHistoryList({
  runs,
  drugLookup,
}: {
  runs: SyncRun[];
  drugLookup: DrugLookup;
}) {
  if (runs.length === 0) {
    return (
      <div className="rounded-lg border p-8 text-center">
        <p className="text-muted-foreground">Brak zapisanych synchronizacji.</p>
      </div>
    );
  }

  const oldestRun = runs[runs.length - 1]!;
  const oldestDate = formatDate(oldestRun.completed_at ?? oldestRun.started_at);

  return (
    <div className="flex flex-col gap-18">
      {runs.map((run) => (
        <SyncRunCard key={run.id} run={run} drugLookup={drugLookup} />
      ))}
      <p className="text-muted-foreground/50 text-center text-xs">
        Przed {oldestDate} leki były aktualizowane raz w tygodniu.
      </p>
    </div>
  );
}
